import { createFeatureSelector, createSelector } from '@ngrx/store';
import { ORDERFORM_FEATURE_KEY } from './orderform.reducer';
import {
  OrderformGlobalSettings,
  OrderformOrderState,
  OrderformSettingsState,
  OrderformState,
} from './orderform.models';
import { UserTrackingState } from '../models/user-tracking.state';

// Lookup the 'Orderform' feature state managed by NgRx
export const getOrderformState = createFeatureSelector<OrderformState>(ORDERFORM_FEATURE_KEY);

export const getOrderformPending = createSelector(getOrderformState, (state: OrderformState) => state.pending);

export const getOrderformSubmitState = createSelector(getOrderformState, (state: OrderformState) => state.submit);
export const getOrderPaymentUrl = createSelector(getOrderformState, (state: OrderformState) => state.paymentUrl);

export const getUserTrackingState = createSelector(
  getOrderformState,
  (state: OrderformState) => state.order.userTracking
);

export const getOrderformSettings = createSelector(getOrderformState, (state: OrderformState) => state.order.settings);

export const getOrderformProductList = createSelector(
  getOrderformSettings,
  (settings: OrderformSettingsState) => settings.product.items
);

export const getOrderformProductSettings = createSelector(
  getOrderformSettings,
  (settings: OrderformSettingsState) => settings.product.settings
);

export const getOrderformGlobalSettings = createSelector(
  getOrderformSettings,
  (settings: OrderformSettingsState) => settings.global
);

export const getOrder = createSelector(getOrderformState, (state: OrderformState) => state.order);

export const getOrderformValidation = createSelector(getOrder, (order: OrderformOrderState) => order.validation);

export const getOrderformMessages = createSelector(getOrder, (order: OrderformOrderState) => order.messages);

export const getOrderItems = createSelector(getOrder, (order: OrderformOrderState) => order.order.items);

export const getOrderPaymentPlanId = createSelector(
  getOrder,
  (order: OrderformOrderState) => order.order.payment_plan_id
);

export const getOrderPreSelectedPaymentPlanId = createSelector(
  getOrder,
  (order: OrderformOrderState) => order.order.preselected_payment_plan_id
);

export const getOrderSelectedPaymentPlan = createSelector(
  getOrder,
  (order: OrderformOrderState) => order.order.selected_plan
);

export const getOrderformPaymentPlanList = createSelector(
  getOrderformState,
  (state: OrderformState) => state.order.payment_plans
);

export const getOrderformConfig = createSelector(getOrderformSettings, (state: OrderformSettingsState) => state.config);

export const getOrderformExitPopup = createSelector(
  getOrderformSettings,
  (state: OrderformSettingsState) => state.exit_popup
);

export const getMerchantName = createSelector(getOrder, (state: OrderformOrderState) => state.merchant);

export const getAffiliateName = createSelector(
  getOrderformSettings,
  (state: OrderformSettingsState) => state.affiliate.name
);

export const getExpressCheckoutConfig = createSelector(
  getOrderformState,
  (state: OrderformState) => state.order.express_checkout
);

export const getIsExpressCheckoutClicked = createSelector(
  getOrderformState,
  (state: OrderformState) => state.isExpressCheckoutClicked
);

export const getOrderformAnonymousSessionId = createSelector(
  getUserTrackingState,
  (state: UserTrackingState) => state.anonymousSessionId
);

export const getOrderformGbId = createSelector(getUserTrackingState, (state: UserTrackingState) => state.gbId);

export const getOrderformIsGdprApplied = createSelector(
  getUserTrackingState,
  (state: UserTrackingState) => state.isGdprApplied
);

export const getOrderformHasCookieConsent = createSelector(
  getUserTrackingState,
  (state: UserTrackingState) => state.hasCookieConsent
);

export const getViewedExperiments = createSelector(
  getUserTrackingState,
  (state: UserTrackingState) => state.viewedExperimentList
);

export const getInitialError = createSelector(getUserTrackingState, (state: UserTrackingState) => state.initialError);

export const getOrderAmount = createSelector(getOrder, (order: OrderformOrderState) => {
  return order.order_summary?.order_final?.is_shown
    ? order.order_summary?.order_final?.gross_1st
    : order.order_summary?.order_total?.first?.gross ?? 0;
});
